import { Box, Text, Stack, Divider } from '@mantine/core';

interface FieldToShow {
  key: string;
  label: string;
  value: any;
  type: string;
}

interface DynamicFieldRendererProps {
  cxItem: any;
  excerpts: any[];
  itemRef?: (el: HTMLDivElement | null) => void;
  fallbackTitle?: string;
  fallbackText?: string;
}

const DynamicFieldRenderer = ({ 
  cxItem, 
  excerpts, 
  itemRef,
  fallbackTitle,
  fallbackText
}: DynamicFieldRendererProps) => {
  
  // Get fields that should be shown in popup from mapping meta
  // Function to format field labels from camelCase/snake_case strings
  const formatLabel = (text: string): string => {
    if (!text) return '';
    
    return text
      // Split camelCase: PMEngagementDetails -> PM Engagement Details
      // Handle consecutive capitals better: PM -> P M, then we'll fix it
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      // Split by underscores: pm_engagement_details -> pm engagement details
      .split('_')
      .join(' ')
      // Remove single character words
      .split(' ')
      .filter(word => word.length > 1)
      .join(' ')
      // Fix common abbreviations that should stay together
      .replace(/\bP M\b/g, 'PM')
      .replace(/\bI D\b/g, 'ID')
      .replace(/\bU R L\b/g, 'URL')
      .replace(/\bA P I\b/g, 'API')
      // Capitalize first letter of each word
      .replace(/\b\w/g, letter => letter.toUpperCase())
      .trim();
  };

  const getFieldsToShow = (): FieldToShow[] => {
    const mappingMeta = cxItem?._mapping_meta;
    const fieldsToShow: FieldToShow[] = [];
    
    // If we have mapping meta, use configured fields
    if (mappingMeta && mappingMeta.fields) {
      // Iterate through all fields in mapping meta
      Object.keys(mappingMeta.fields).forEach(fieldKey => {
        const fieldConfig = mappingMeta.fields[fieldKey];
        
        // Check if field should be shown in popup
        if (fieldConfig?.showInPopup === true) {
          // Use the fieldPath or key to get the actual field name in cxItem
          const actualFieldName = fieldConfig.fieldPath || fieldConfig.key || fieldConfig.name;
          const fieldValue = cxItem[actualFieldName];
          
          if (fieldValue) {
            fieldsToShow.push({
              key: actualFieldName,
              label: formatLabel(fieldConfig.name || fieldConfig.label || actualFieldName),
              value: fieldValue,
              type: fieldConfig.type || 'text'
            });
          }
        }
      });
    }

    return fieldsToShow;
  };

  // Function to merge overlapping or contiguous ranges
  const mergeRanges = (ranges: any[]) => {
    if (ranges.length === 0) return [];

    ranges.sort((a, b) => a.excerptFirstChar - b.excerptFirstChar);

    const mergedRanges = [ranges[0]];

    for (let i = 1; i < ranges.length; i++) {
      const lastRange = mergedRanges[mergedRanges.length - 1];
      const currentRange = ranges[i];

      if (currentRange.excerptFirstChar <= lastRange.excerptLastChar + 1) {
        lastRange.excerptLastChar = Math.max(lastRange.excerptLastChar, currentRange.excerptLastChar);
      } else {
        mergedRanges.push(currentRange);
      }
    }

    return mergedRanges;
  };

  // Function to highlight excerpts in HTML content
  const highlightExcerpts = (htmlContent: string, excerpts: any[]) => {
    if (!excerpts || excerpts.length === 0) {
      return (
        <div 
          dangerouslySetInnerHTML={{ __html: htmlContent }}
          style={{ 
            whiteSpace: 'pre-wrap',
            lineHeight: '1.5'
          }}
        />
      );
    }

    let processedContent = htmlContent;
    
    // Process each excerpt and find it in the content by text matching
    excerpts.forEach((excerpt, index) => {
      if (excerpt.excerpt) {
        const excerptText = excerpt.excerpt.trim();
        
        // First, check if the excerpt text exists anywhere in the HTML (even with tags)
        const plainTextContent = htmlContent.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
        
        if (!plainTextContent.toLowerCase().includes(excerptText.toLowerCase())) {
          return; // Skip this excerpt if not found
        }
        
        // Try multiple strategies to find and highlight the excerpt text
        
        // Strategy 1: Direct text match (case insensitive)
        const directMatch = new RegExp(
          excerptText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 
          'gi'
        );
        
        if (processedContent.match(directMatch)) {
          processedContent = processedContent.replace(directMatch, (match) => {
            return `<span style="background-color: #FFF9A0; color: black; border-radius: 6px; padding: 2px 4px;">${match}</span>`;
          });
          return; // Found direct match, skip other strategies
        }
        
        // Strategy 2: Flexible word-by-word matching allowing HTML tags between words and punctuation
        const words = excerptText.split(/\s+/);
        if (words.length > 1) {
          // More flexible pattern that handles punctuation inside HTML tags
          const flexiblePattern = words
            .map((word: string) => {
              // Remove punctuation from the end of words for more flexible matching
              const cleanWord = word.replace(/[.,;:!?]+$/, '');
              const punctuation = word.slice(cleanWord.length);
              const escapedWord = cleanWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              const escapedPunctuation = punctuation.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              
              // Allow HTML tags around the word and punctuation
              if (punctuation) {
                return `(?:<[^>]*>)*${escapedWord}(?:<[^>]*>)*${escapedPunctuation}(?:<[^>]*>)*`;
              } else {
                return `(?:<[^>]*>)*${escapedWord}(?:<[^>]*>)*`;
              }
            })
            .join('\\s*'); // Allow whitespace between word patterns
          
          const flexibleRegex = new RegExp(flexiblePattern, 'gi');
          
          if (processedContent.match(flexibleRegex)) {
            processedContent = processedContent.replace(flexibleRegex, (match) => {
              return `<span style="background-color: #FFF9A0; color: black; border-radius: 6px; padding: 2px 4px;">${match}</span>`;
            });
            return; // Found flexible match, skip other strategies
          }
        }
        
        // Strategy 3: Try to find partial matches (at least 70% of the words)
        if (words.length > 2) {
          const minWordsToMatch = Math.ceil(words.length * 0.7);
          for (let i = 0; i <= words.length - minWordsToMatch; i++) {
            const partialWords = words.slice(i, i + minWordsToMatch);
            const partialPattern = partialWords
              .map((word: string) => {
                const cleanWord = word.replace(/[.,;:!?]+$/, '');
                return cleanWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              })
              .join('\\s*(?:<[^>]*>)*\\s*');
            
            const partialRegex = new RegExp(partialPattern, 'gi');
            
            if (processedContent.match(partialRegex)) {
              processedContent = processedContent.replace(partialRegex, (match) => {
                return `<span style="background-color: #FFF9A0; color: black; border-radius: 6px; padding: 2px 4px;">${match}</span>`;
              });
              break; // Found partial match, stop trying other partial matches
            }
          }
        }
        
        // Strategy 4: Aggressive text-based replacement (convert to plain text, find, then map back)
        if (plainTextContent.toLowerCase().includes(excerptText.toLowerCase())) {
          // Find the start position in plain text
          const startPos = plainTextContent.toLowerCase().indexOf(excerptText.toLowerCase());
          if (startPos !== -1) {
            const endPos = startPos + excerptText.length;
            
            // Convert HTML to array of characters with their HTML context
            const htmlChars = [];
            let plainIndex = 0;
            let i = 0;
            
            while (i < htmlContent.length) {
              if (htmlContent[i] === '<') {
                // Skip HTML tag
                let tagEnd = htmlContent.indexOf('>', i);
                if (tagEnd === -1) tagEnd = htmlContent.length;
                htmlChars.push({ type: 'tag', content: htmlContent.substring(i, tagEnd + 1) });
                i = tagEnd + 1;
              } else if (htmlContent[i].match(/\s/)) {
                // Handle whitespace
                let wsEnd = i;
                while (wsEnd < htmlContent.length && htmlContent[wsEnd].match(/\s/)) {
                  wsEnd++;
                }
                htmlChars.push({ 
                  type: 'text', 
                  content: htmlContent.substring(i, wsEnd),
                  plainIndex: plainIndex
                });
                plainIndex += 1; // Normalize whitespace to single space in plain text
                i = wsEnd;
              } else {
                // Regular character
                htmlChars.push({ 
                  type: 'text', 
                  content: htmlContent[i],
                  plainIndex: plainIndex
                });
                plainIndex++;
                i++;
              }
            }
            
            // Find where to insert highlight tags
            let result = '';
            let highlightStarted = false;
            
            for (const char of htmlChars) {
              if (char.type === 'tag') {
                result += char.content;
              } else {
                if (!highlightStarted && char.plainIndex !== undefined && char.plainIndex >= startPos) {
                  result += '<span style="background-color: #FFF9A0; color: black; border-radius: 6px; padding: 2px 4px;">';
                  highlightStarted = true;
                }
                
                result += char.content;
                
                if (highlightStarted && char.plainIndex !== undefined && char.plainIndex >= endPos - 1) {
                  result += '</span>';
                  highlightStarted = false;
                }
              }
            }
            
            processedContent = result;
            return; // Found and applied aggressive match
          }
        }
      }
    });

    return (
      <div 
        dangerouslySetInnerHTML={{ __html: processedContent }}
        style={{ 
          whiteSpace: 'pre-wrap',
          lineHeight: '1.5'
        }}
      />
    );
  };

  // Format field value based on type
  const formatFieldValue = (value: any, type: string) => {
    if (!value) return '';
    
    switch (type) {
    case 'date':
      try {
        return new Date(value).toLocaleDateString();
      } catch {
        return value;
      }
    case 'number':
      return typeof value === 'number' ? value.toLocaleString() : value;
    default:
      return value;
    }
  };

  const fieldsToShow = getFieldsToShow();

  if (fieldsToShow.length === 0) {
    return (
      <Box
        ref={itemRef}
        ml={35}
        mt={5}
        px={10}
        py={8}
        sx={{ borderRadius: "11px" }}
      >
        <Text color="dimmed" fz={14}>
          No content available to display
        </Text>
      </Box>
    );
  }

  return (
    <Box
      ref={itemRef}
      ml={35}
      mt={5}
      px={10}
      py={8}
      sx={{ borderRadius: "11px" }}
    >
      <Stack spacing="md">
        {fieldsToShow.map((field, index) => (
          <Box key={field.key}>
            <Text fw={600} fz={14} mb={4} color="#333">
              {field.label}
            </Text>
            <Box
              style={{ 
                fontSize: '14px',
                lineHeight: '1.5'
              }}
            >
              {typeof field.value === 'string' && field.value.includes('<') ? 
                highlightExcerpts(field.value, excerpts.filter(excerpt => 
                  // Check if excerpt text exists anywhere in the field content
                  excerpt.excerpt && field.value.includes(excerpt.excerpt.trim())
                )) :
                <Text fz={14} style={{ whiteSpace: 'pre-wrap' }}>
                  {formatFieldValue(field.value, field.type)}
                </Text>
              }
            </Box>
            {index < fieldsToShow.length - 1 && <Divider mt="md" />}
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default DynamicFieldRenderer;
